import { add } from 'date-fns';
import { ReactElement } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  BaseTextFieldController,
  DatePicker,
  MediaUploadZone,
  Form,
  FormAttachments,
  FormGroup,
  RichEditorController,
  SelectController,
  FormCancelButton,
  FormSubmitButton,
  Spinner,
  NewConfirmDialog,
} from 'components';
import { IMAGE_MIME_TYPES, VIDEO_MIME_TYPES } from 'components/MediaUploadZone/constants';
import { EQUIPMENT_FORM_FIELDS_LENGTH } from 'constants/fieldsLength';
import { useWindowSize } from 'hooks';
import { textFieldStyles, textFieldMobileStyles, richEditorStyles } from 'styles';
import { EquipmentFormValues, PostDraft, User } from 'types';
import { required } from 'validations';

import { categoryOptions, initialWindowSizeProps } from '../constants';
import { CustomFields } from '../CustomFields';
import { useEquipmentForm } from './hooks';
import { CheckboxInput, CheckboxW, LabelTitle, LabelW, FormContainer } from './styled';
import { userSelectSx } from './styles';
import {
  commentFieldRules,
  titleFieldRules,
  onlyFutureDates,
  onlyPastAndCurrentDates,
  invalidDate,
} from './validation';

interface EquipmentFormProps {
  draft?: PostDraft | null;
  isLoading: boolean;
  usersList: User[];
  defaultValues: (isDefaultEditable: boolean, draft?: PostDraft) => EquipmentFormValues;
  onSubmit: (data: EquipmentFormValues, attachmentIds: string[]) => void;
  mode?: 'create' | 'edit';
  isRequestForm?: boolean;
  onClose?: () => void;
}

export function EquipmentForm({
  draft,
  isLoading: isEquipmentMutationLoading,
  usersList,
  defaultValues,
  onSubmit,
  mode = 'create',
  isRequestForm = false,
  onClose,
}: EquipmentFormProps): ReactElement {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize(initialWindowSizeProps);
  const isEditMode = mode === 'edit';

  const {
    isLoading,
    isSubmitting,
    isCurrentUserProfile,
    isSelectedCurrentUser,
    isCreateFromProfile,
    isManager,
    isSubmitted,
    isDirty,
    control,
    usersOptions,
    assigneeOptions,
    attachedImages,
    attachedDocs,
    attachedVideos,
    getValues,
    handleFormSubmit,
    register,
    setError,
    handleAddImages,
    handleAddDocuments,
    handleAddVideos,
    handleDeleteAttachment,
    isOpen,
    handleSubmitConfirmModal,
    handleCloseConfirmModal,
  } = useEquipmentForm({
    draft,
    isEquipmentMutationLoading,
    isEditMode,
    usersList,
    defaultValues,
    onSubmit,
  });
  const navigate = useNavigate();

  const isSubmitButtonDisabled =
    (isEditMode && !isDirty) || isSubmitting || isEquipmentMutationLoading;
  const formGroupMarginBottom = isMobile ? 16 : 24;
  const fieldStyles = isMobile ? textFieldMobileStyles : textFieldStyles;
  const todayDate = new Date();
  const tomorrowDate = add(todayDate, {
    days: 1,
  });

  const handleUploadAll = async (files: FileList) => {
    const toFileList = (arr: File[]): FileList => {
      const dt = new DataTransfer();
      arr.forEach((f) => dt.items.add(f));
      return dt.files;
    };
    const imageFiles = Array.from(files).filter((f) => IMAGE_MIME_TYPES.includes(f.type));
    const videoFiles = Array.from(files).filter((f) => VIDEO_MIME_TYPES.includes(f.type));
    const docFiles = Array.from(files).filter(
      (f) => !IMAGE_MIME_TYPES.includes(f.type) && !VIDEO_MIME_TYPES.includes(f.type),
    );

    if (imageFiles.length) await handleAddImages(toFileList(imageFiles));
    if (videoFiles.length) await handleAddVideos(toFileList(videoFiles));
    if (docFiles.length) await handleAddDocuments(toFileList(docFiles));
  };

  return (
    <>
      {(isLoading || isSubmitting) && <Spinner />}

      <FormContainer>
        <Form onSubmit={handleFormSubmit}>
          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('equipments.create.form.placeholders.title')}</LabelTitle>
            <BaseTextFieldController
              name="title"
              control={control}
              placeholder={t('equipments.create.form.placeholders.title')}
              sx={fieldStyles}
              maxLength={EQUIPMENT_FORM_FIELDS_LENGTH.title.max}
              rules={titleFieldRules}
            />
          </FormGroup>
          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('equipments.create.form.placeholders.category')}</LabelTitle>

            <SelectController
              name="category"
              control={control}
              placeholder={t('equipments.create.form.placeholders.category')}
              options={categoryOptions}
              rules={required}
              type="category"
            />
          </FormGroup>
          {isManager && (
            <FormGroup marginBottom={formGroupMarginBottom}>
              <LabelTitle>{t('equipments.create.form.placeholders.user')}</LabelTitle>
              <SelectController
                name="user"
                control={control}
                placeholder={t('equipments.create.form.placeholders.user')}
                options={usersOptions}
                rules={required}
                disabled={isCurrentUserProfile || isCreateFromProfile || isEditMode}
                sx={userSelectSx}
                type="user"
              />
            </FormGroup>
          )}
          {isRequestForm && (
            <FormGroup marginBottom={formGroupMarginBottom}>
              <LabelTitle>{t('equipments.create.form.placeholders.assignee')}</LabelTitle>
              <SelectController
                name="assignee"
                control={control}
                placeholder={t('equipments.create.form.placeholders.assignee')}
                options={assigneeOptions}
                rules={required}
                sx={userSelectSx}
              />
            </FormGroup>
          )}
          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('equipments.create.form.placeholders.expirationDate')}</LabelTitle>
            <DatePicker
              {...register('expireAt', {
                validate: {
                  invalidDate,
                  onlyFutureDates,
                },
              })}
              control={control}
              placeholder={t('equipments.create.form.placeholders.expirationDate')}
              sx={fieldStyles}
              minDate={tomorrowDate}
            />
          </FormGroup>
          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('equipments.create.form.placeholders.orderedOn')}</LabelTitle>
            <DatePicker
              {...register('orderedAt', {
                validate: {
                  invalidDate,
                  onlyPastAndCurrentDates,
                },
              })}
              control={control}
              placeholder={t('equipments.create.form.placeholders.orderedOn')}
              sx={fieldStyles}
              maxDate={todayDate}
            />
          </FormGroup>
          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('equipments.create.form.placeholders.serialNumber')}</LabelTitle>
            <BaseTextFieldController
              name="serial"
              control={control}
              placeholder={t('equipments.create.form.placeholders.serialNumber')}
              sx={fieldStyles}
              maxLength={EQUIPMENT_FORM_FIELDS_LENGTH.serial.max}
            />
          </FormGroup>

          <FormGroup marginBottom={formGroupMarginBottom} row>
            <LabelTitle>{t('equipments.create.form.placeholders.comment')}</LabelTitle>
            <RichEditorController
              name="comment"
              control={control}
              placeholder={t('equipments.create.form.placeholders.comment')}
              maxLength={EQUIPMENT_FORM_FIELDS_LENGTH.comment.max}
              rules={commentFieldRules}
              sx={richEditorStyles(isMobile)}
            />
          </FormGroup>

          <FormGroup marginBottom={formGroupMarginBottom} row>
            {!isMobile && (
              <FormAttachments
                attachedImages={attachedImages}
                attachedDocs={attachedDocs}
                attachedVideos={attachedVideos}
                handleDeleteAttachment={handleDeleteAttachment}
                hasDownloadDoc={isEditMode}
                draftId={draft?.id}
              />
            )}

            {isMobile && (
              <FormAttachments
                attachedImages={attachedImages}
                attachedDocs={attachedDocs}
                attachedVideos={attachedVideos}
                handleDeleteAttachment={handleDeleteAttachment}
                hasDownloadDoc={isEditMode}
                draftId={draft?.id}
              />
            )}
            <MediaUploadZone onUpload={(files) => handleUploadAll(files)} />
          </FormGroup>
          <LabelTitle>{t('equipments.create.form.placeholders.customFields')}</LabelTitle>
          <CustomFields
            isSubmitted={isSubmitted}
            setError={setError}
            control={control}
            getValues={getValues}
          />

          {isManager && (
            <Controller
              control={control}
              name="isEditable"
              render={({ field: { value, onChange } }) => {
                return (
                  <CheckboxW>
                    <CheckboxInput
                      disabled={isCurrentUserProfile || isSelectedCurrentUser}
                      checked={value}
                      value={value}
                      onChange={onChange}
                      color="info"
                    />
                    <LabelW>{t('equipments.create.form.placeholders.editDelete')}</LabelW>
                  </CheckboxW>
                );
              }}
            />
          )}

          {!isMobile && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '32px',
              }}
            >
              <FormCancelButton
                variant="outlined"
                onClick={() => (onClose ? onClose() : navigate(-1))}
              >
                {t('common.cancel')}
              </FormCancelButton>
              <FormSubmitButton type="submit" disabled={isSubmitButtonDisabled}>
                {isEditMode ? t('common.save') : t('common.create')}
              </FormSubmitButton>
            </div>
          )}
        </Form>

        {isMobile && (
          <div
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}
          >
            <FormCancelButton
              variant="outlined"
              onClick={() => (onClose ? onClose() : navigate(-1))}
            >
              {t('common.cancel')}
            </FormCancelButton>
            <FormSubmitButton disabled={isSubmitButtonDisabled} onClick={handleFormSubmit}>
              {isEditMode ? t('common.save') : t('common.create')}
            </FormSubmitButton>
          </div>
        )}
      </FormContainer>

      {isOpen && (
        <NewConfirmDialog
          title={t('task.create.modal.title')}
          subTitle={t('task.create.modal.exit')}
          open={isOpen}
          onClose={handleCloseConfirmModal}
          onConfirm={handleSubmitConfirmModal}
          submitLabel={t('task.create.modal.successButton')}
          cancelLabel={t('task.create.modal.cancelButton')}
        />
      )}
    </>
  );
}
